// GAVION: scanner-proof landing page of invite / recovery links
// (#/activer?token_hash=…&type=invite|recovery). A GoTrue verify URL is a
// one-time GET that link-preview bots (Microsoft Teams, Defender Safe Links)
// consume before the invitee clicks. Here the token hash is only verified when
// the person clicks the button: nothing runs on page load, since bots load
// pages and some run JavaScript. On success the new session's tokens go to
// set-password exactly like ra-supabase handleCallback does.
// GAVION self-service password reset: a link emailed by gavion-password-reset
// is #/activer?reset=<token>. The click first spends that token (/redeem: one
// use, 30 minutes) and only then gets the Supabase Auth recovery token, verified at
// once, the same way.
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/supabase/layout";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import { RESET_TOKEN_RE, redeemPasswordReset } from "./passwordResetApi";

type ActivationType = "invite" | "recovery";

const COPY: Record<
  ActivationType,
  { title: string; text: string; button: string }
> = {
  invite: {
    title: "Activation du compte",
    text: "Bienvenue dans Gavion CRM. Clique sur le bouton pour activer ton compte et choisir ton mot de passe.",
    button: "Activer mon compte",
  },
  recovery: {
    title: "Nouveau mot de passe",
    text: "Clique pour choisir un nouveau mot de passe.",
    button: "Choisir mon mot de passe",
  },
};

export const INVALID_LINK_MESSAGE =
  "Ce lien n'est plus valide. Demande une nouvelle invitation à ton administrateur.";

export const RESET_INVALID_MESSAGE =
  "Ce lien n'est plus valide : il sert une seule fois, pendant 30 minutes. Demande un nouveau lien depuis « Mot de passe oublié ? ».";
export const RESET_ERROR_MESSAGE =
  "Le lien n'a pas pu être vérifié. Réessaie dans un instant.";

const parseType = (value: string | null): ActivationType | null =>
  value === "invite" || value === "recovery" ? value : null;

export const ActivateAccountPage = ({
  redeem = redeemPasswordReset,
}: {
  /** Test seam. */
  redeem?: typeof redeemPasswordReset;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const resetToken = params.get("reset");
  const selfReset = resetToken !== null;
  const tokenHash = params.get("token_hash");
  const type: ActivationType | null = selfReset
    ? "recovery"
    : parseType(params.get("type"));
  const usable = selfReset
    ? RESET_TOKEN_RE.test(resetToken)
    : Boolean(tokenHash && type);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState<false | "invalid" | "error">(false);

  const activate = async () => {
    if (!usable || !type || loading) return;
    setLoading(true);
    setFailed(false);
    try {
      let hash = tokenHash ?? "";
      if (selfReset) {
        const redeemed = await redeem(resetToken);
        if (!redeemed.ok) {
          setFailed(redeemed.reason === "invalid_link" ? "invalid" : "error");
          return;
        }
        hash = redeemed.tokenHash;
      }
      const { data, error } = await getSupabaseClient().auth.verifyOtp({
        token_hash: hash,
        type,
      });
      const session = data?.session;
      if (error || !session?.access_token || !session.refresh_token) {
        setFailed("invalid");
        return;
      }
      const search = new URLSearchParams({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
        type,
      });
      navigate(`/set-password?${search.toString()}`, { replace: true });
    } catch {
      setFailed(selfReset ? "error" : "invalid");
    } finally {
      setLoading(false);
    }
  };

  const invalidMessage = selfReset
    ? RESET_INVALID_MESSAGE
    : INVALID_LINK_MESSAGE;
  const forgotLink = selfReset ? (
    <p className="text-center text-sm">
      <Link className="underline" to="/forgot-password">
        Mot de passe oublié ?
      </Link>
    </p>
  ) : null;

  if (!usable || !type) {
    return (
      <Layout>
        <p className="text-center">{invalidMessage}</p>
        {forgotLink}
      </Layout>
    );
  }

  const copy = COPY[type];
  return (
    <Layout>
      <div className="flex flex-col space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">{copy.title}</h1>
        <p className="text-sm text-muted-foreground">{copy.text}</p>
      </div>
      {failed ? (
        <p role="alert" className="text-center text-sm text-destructive">
          {failed === "error" ? RESET_ERROR_MESSAGE : invalidMessage}
        </p>
      ) : null}
      {failed === "invalid" ? forgotLink : null}
      <Button
        type="button"
        className="cursor-pointer"
        disabled={loading}
        onClick={activate}
      >
        {copy.button}
      </Button>
    </Layout>
  );
};

ActivateAccountPage.path = "activer";
